"use client";

import { cn } from "@/utils/cn";
import { CONTACTS } from "@consts/contacts";
import { SITE } from "@consts/site";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";

interface Props {
	className?: string;
}

// Расстояние до карты, с которого начинается её загрузка
const LOAD_MARGIN = "600px";
const MAP_ZOOM = 17;
const MARKER_COLOR = "#2D8447";

// Карта на API Яндекс Карт 2.1: без кнопок и карточки организации, только метка.
// Тёмной темы у этой версии API нет, поэтому слой с тайлами инвертируется
// фильтром; метка и копирайт Яндекса лежат в других слоях и остаются как есть
export default function ContactsMap({ className }: Props) {
	const rootRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<HTMLDivElement>(null);
	const [isNear, setIsNear] = useState(false);
	const [isApiReady, setIsApiReady] = useState(false);

	// API тянет десятки запросов и ставит cookie Яндекса,
	// поэтому подключаем его, только когда посетитель долистал до карты
	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;

				setIsNear(true);
				observer.disconnect();
			},
			{ rootMargin: LOAD_MARGIN }
		);

		observer.observe(root);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!isApiReady) return;

		const { latitude, longitude, url } = CONTACTS.map;
		let map: YandexMap | undefined;
		let logoObserver: MutationObserver | undefined;
		let isCancelled = false;

		ymaps.ready(() => {
			if (isCancelled || !mapRef.current) return;

			map = new ymaps.Map(
				mapRef.current,
				{ center: [latitude, longitude], zoom: MAP_ZOOM, controls: [] },
				{
					suppressMapOpenBlock: true,
					yandexMapDisablePoiInteractivity: true,
				}
			);

			// Колесо и палец должны листать страницу, а не карту
			map.behaviors.disable("scrollZoom");
			if (window.matchMedia("(pointer: coarse)").matches) {
				map.behaviors.disable("drag");
			}

			const marker = new ymaps.Placemark(
				[latitude, longitude],
				{ iconCaption: SITE.name },
				{ preset: "islands#dotIcon", iconColor: MARKER_COLOR }
			);

			marker.events.add("click", () => {
				window.open(url, "_blank", "noopener,noreferrer");
			});

			map.geoObjects.add(marker);

			// У ссылки-логотипа Яндекса в углу карты нет текста — подписываем её для
			// скринридера. Яндекс создаёт её не сразу и может пересоздать, поэтому следим
			const root = mapRef.current;
			const labelLogo = () => {
				const link = root.querySelector("a[class*='copyright__logo']");

				if (link && !link.hasAttribute("aria-label")) {
					link.setAttribute("aria-label", "Яндекс Карты");
				}
			};

			labelLogo();
			logoObserver = new MutationObserver(labelLogo);
			logoObserver.observe(root, { childList: true, subtree: true });
		});

		return () => {
			isCancelled = true;
			logoObserver?.disconnect();
			map?.destroy();
		};
	}, [isApiReady]);

	const widgetUrl = `https://api-maps.yandex.ru/2.1/?apikey=${CONTACTS.map.apiKey}&lang=ru_RU`;

	return (
		<div ref={rootRef} className={cn("overflow-hidden", className)}>
			<div
				ref={mapRef}
				role="application"
				aria-label={`${SITE.name} на карте`}
				className="size-full"
			/>

			{isNear && (
				<Script src={widgetUrl} onReady={() => setIsApiReady(true)} />
			)}
		</div>
	);
}
